import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { CATS, SRC_TO_CAT, catOf } from '@/components/WelcomeCalendar';
import {
  ChevronLeft, ChevronRight, ExternalLink, CalendarDays, Plus, Search,
  AlertTriangle, CalendarClock, ArrowLeft, DollarSign,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;          // m 0-based
const mmddyy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${m}/${d}/${y.slice(2)}`; };
const prettyDate = (s) => { if (!s) return ''; const [y, m, d] = s.split('-').map(Number); return `${DOW[new Date(y, m - 1, d).getDay()]} ${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`; };
const etTodayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const addDays = (isoStr, n) => { const [y, m, d] = isoStr.split('-').map(Number); const dt = new Date(Date.UTC(y, m - 1, d + n)); return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`; };

const monthCells = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const weekDays = (anchorISO) => {
  const [y, m, d] = anchorISO.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const sunday = new Date(base); sunday.setDate(base.getDate() - base.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(sunday); dt.setDate(sunday.getDate() + i);
    return iso(dt.getFullYear(), dt.getMonth(), dt.getDate());
  });
};

const catKey = (source) => SRC_TO_CAT[source] || 'other';
const CAP = 3;

const CalendarModule = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const today = etTodayISO();

  const [view, setView] = useState('month');
  const [sel, setSel] = useState(() => { const [y, m] = today.split('-'); return { year: +y, month: +m - 1 }; });
  const [weekAnchor, setWeekAnchor] = useState(today);
  const [events, setEvents] = useState([]);
  const [attention, setAttention] = useState({ upcoming: [], overdue: [], unscheduled: [] });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCats, setActiveCats] = useState(() => new Set());
  const [detailDate, setDetailDate] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', date: today, end_date: '', location: '', description: '' });
  const [saving, setSaving] = useState(false);

  const range = useMemo(() => {
    if (view === 'list') return { start: today, end: addDays(today, 90) };
    if (view === 'week') { const w = weekDays(weekAnchor); return { start: w[0], end: w[6] }; }
    const start = iso(sel.year, sel.month, 1);
    const end = iso(sel.year, sel.month, new Date(sel.year, sel.month + 1, 0).getDate());
    return { start, end };
  }, [view, sel, weekAnchor, today]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadEvents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/events`, { params: range, headers: authHeaders });
      setEvents(r.data.events || []);
    } catch { setEvents([]); } finally { setLoading(false); }
  }, [token, range, authHeaders]);

  const loadAttention = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/calendar/attention`, { headers: authHeaders });
      setAttention(r.data || { upcoming: [], overdue: [], unscheduled: [] });
    } catch { /* non-fatal */ }
  }, [token, authHeaders]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadAttention(); }, [loadAttention]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/company-events/can-manage`, { headers: authHeaders })
      .then((r) => setCanManage(!!r.data?.can_manage)).catch(() => {});
  }, [token, authHeaders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (activeCats.size && !activeCats.has(catKey(e.source))) return false;
      if (!q) return true;
      return `${e.title} ${e.detail || ''} ${e.source_label || ''}`.toLowerCase().includes(q);
    });
  }, [events, query, activeCats]);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of filtered) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [filtered]);

  const presentCats = useMemo(() => {
    const s = new Set(events.map((e) => catKey(e.source)));
    return Object.keys(CATS).filter((k) => s.has(k));
  }, [events]);

  const toggleCat = (k) => setActiveCats((prev) => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const step = (delta) => {
    if (view === 'week') { setWeekAnchor((w) => addDays(w, delta * 7)); return; }
    setSel((s) => {
      let y = s.year, m = s.month + delta;
      if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
      return { year: y, month: m };
    });
  };
  const goToday = () => {
    const [y, m] = today.split('-'); setSel({ year: +y, month: +m - 1 }); setWeekAnchor(today);
  };

  const headerLabel = view === 'week'
    ? (() => { const w = weekDays(weekAnchor); return `${prettyDate(w[0])} – ${prettyDate(w[6])}`; })()
    : view === 'list' ? 'Next 90 days' : `${MONTHS[sel.month]} ${sel.year}`;

  const openEvent = (e) => setDetailEvent(e);

  const submitCreate = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Title and date are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/company-events`, {
        title: form.title.trim(), date: form.date,
        end_date: form.end_date || null, location: form.location || null,
        description: form.description || null,
      }, { headers: authHeaders });
      toast.success('Company event added');
      setShowCreate(false);
      setForm({ title: '', date: today, end_date: '', location: '', description: '' });
      loadEvents(); loadAttention();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save event');
    } finally { setSaving(false); }
  };

  const Chip = ({ k }) => {
    const c = CATS[k]; const on = activeCats.has(k) || activeCats.size === 0;
    return (
      <button data-testid={`calendar-filter-${k}`} onClick={() => toggleCat(k)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 text-slate-400 hover:text-slate-200'}`}>
        <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: c.color }} />
        {c.name}
      </button>
    );
  };

  const EventPill = ({ e, onClick }) => {
    const c = catOf(e.source);
    return (
      <button onClick={onClick} data-testid={`calendar-event-${e.record_id || e.title}`}
        className="flex w-full items-center rounded-[3px] px-1 py-[1px] text-left text-[9px] font-semibold"
        style={{ backgroundColor: c.color, color: c.text }}>
        <span className="min-w-0 truncate">{e.title}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#0B1F38] text-slate-100" data-testid="calendar-module">
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} data-testid="calendar-back"
              className="rounded-lg border border-white/15 p-2 text-slate-200 hover:bg-white/10" aria-label="Back to hub">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                <CalendarDays size={24} className="text-[#8FC93A]" /> Company Calendar
              </h1>
              <p className="text-sm text-slate-400">Company events & obligations {loading && <span className="text-slate-500">· loading…</span>}</p>
            </div>
          </div>
          {canManage && (
            <button onClick={() => setShowCreate(true)} data-testid="calendar-new-event-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8FC93A] px-4 py-2 text-sm font-bold text-[#0E2A47] transition-colors hover:bg-[#7bb62f]">
              <Plus size={16} /> New event
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-white/15 p-0.5" data-testid="calendar-view-switch">
              {['month', 'week', 'list'].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold capitalize transition-colors ${view === v ? 'bg-[#8FC93A] text-[#0E2A47]' : 'text-slate-300 hover:bg-white/10'}`}>
                  {v}
                </button>
              ))}
            </div>
            {view !== 'list' && (
              <div className="flex items-center gap-1">
                <button onClick={() => step(-1)} data-testid="calendar-prev" className="rounded-lg border border-white/15 p-1.5 hover:bg-white/10"><ChevronLeft size={18} /></button>
                <button onClick={() => step(1)} data-testid="calendar-next" className="rounded-lg border border-white/15 p-1.5 hover:bg-white/10"><ChevronRight size={18} /></button>
                <button onClick={goToday} data-testid="calendar-today" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-bold hover:bg-white/10">Today</button>
              </div>
            )}
            <div className="text-base font-extrabold tracking-tight text-white sm:text-lg" data-testid="calendar-range-label">{headerLabel}</div>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="calendar-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search events…" className="h-9 border-white/15 bg-white/5 pl-9 text-sm text-white placeholder:text-slate-500" />
          </div>
        </div>

        {/* Filters */}
        {presentCats.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5" data-testid="calendar-filters">
            {presentCats.map((k) => <Chip key={k} k={k} />)}
          </div>
        )}

        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Main */}
          <div className="min-w-0 flex-1">
            {view === 'month' && <MonthGrid sel={sel} today={today} byDate={byDate} onDay={setDetailDate} onEvent={openEvent} />}
            {view === 'week' && <WeekView days={weekDays(weekAnchor)} today={today} byDate={byDate} onEvent={openEvent} />}
            {view === 'list' && <ListView events={filtered} today={today} onEvent={openEvent} />}

            {/* Legend */}
            <div className="mt-5 rounded-xl bg-white px-3 py-2.5" data-testid="calendar-legend">
              <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {Object.keys(CATS).map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] ring-1 ring-black/10" style={{ backgroundColor: CATS[k].color }} />
                    <span className="text-[11px] font-medium text-slate-700">{CATS[k].name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full shrink-0 space-y-4 lg:w-[320px]">
            <Panel title="Needs attention" icon={<AlertTriangle size={16} className="text-amber-400" />} testid="calendar-attention">
              {attention.overdue?.length === 0 && attention.upcoming?.length === 0 && (
                <p className="py-3 text-center text-xs text-slate-400" data-testid="calendar-attention-empty">Nothing overdue or due soon.</p>
              )}
              {attention.overdue?.map((e, i) => <AttnRow key={`o${i}`} e={e} overdue onClick={() => openEvent(e)} />)}
              {attention.upcoming?.map((e, i) => <AttnRow key={`u${i}`} e={e} onClick={() => openEvent(e)} />)}
            </Panel>

            <Panel title="Needs scheduling" icon={<CalendarClock size={16} className="text-sky-400" />} testid="calendar-unscheduled">
              {(!attention.unscheduled || attention.unscheduled.length === 0) && (
                <p className="py-3 text-center text-xs text-slate-400" data-testid="calendar-unscheduled-empty">No agreements are missing a date.</p>
              )}
              {attention.unscheduled?.map((u, i) => (
                <button key={i} onClick={() => u.route && navigate(u.route)} data-testid={`calendar-unscheduled-${i}`}
                  className="block w-full rounded-lg border border-white/10 bg-white/[0.03] p-2.5 text-left hover:bg-white/[0.07]">
                  <div className="text-xs font-bold text-white">{u.title}</div>
                  <div className="text-[11px] text-amber-300">{u.reason}</div>
                  {u.detail && <div className="text-[11px] text-slate-400">{u.detail}</div>}
                </button>
              ))}
            </Panel>
          </div>
        </div>
      </div>

      {/* Day detail */}
      <Dialog open={!!detailDate} onOpenChange={(o) => !o && setDetailDate(null)}>
        <DialogContent className="max-w-md" data-testid="calendar-day-detail-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">{prettyDate(detailDate)}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {(byDate[detailDate] || []).length === 0 && <p className="py-6 text-center text-sm text-slate-500">No items on this date.</p>}
            {(byDate[detailDate] || []).map((e, i) => <DetailCard key={i} e={e} onOpen={() => { setDetailDate(null); openEvent(e); }} />)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Event detail */}
      <Dialog open={!!detailEvent} onOpenChange={(o) => !o && setDetailEvent(null)}>
        <DialogContent className="max-w-md" data-testid="calendar-event-detail-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">{detailEvent?.title}</DialogTitle></DialogHeader>
          {detailEvent && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="h-3.5 w-3.5 rounded-[3px] ring-1 ring-black/10" style={{ backgroundColor: catOf(detailEvent.source).color }} />
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{detailEvent.source_label}</span>
                {detailEvent.status && <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{detailEvent.status}</span>}
              </div>
              <div className="text-sm text-slate-600">{prettyDate(detailEvent.date)}</div>
              {detailEvent.detail && <div className="text-sm text-slate-700">{detailEvent.detail}</div>}
              {detailEvent.source === 'payroll' && <div className="text-[11px] italic text-slate-500">Scheduled payroll date — not confirmation payroll was processed or paid.</div>}
              {detailEvent.route ? (
                <button data-testid="calendar-event-open" onClick={() => { const r = detailEvent.route; setDetailEvent(null); navigate(r); }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a5f]">
                  <ExternalLink size={13} /> Open record
                </button>
              ) : <div className="text-[11px] italic text-slate-400">No linked destination for this item yet.</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create company event */}
      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent className="max-w-md" data-testid="calendar-create-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">New company event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title *"><Input data-testid="create-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Company all-hands" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date *"><Input data-testid="create-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              <Field label="End date"><Input data-testid="create-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Location"><Input data-testid="create-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Optional" /></Field>
            <Field label="Description"><textarea data-testid="create-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm text-slate-900" placeholder="Optional" /></Field>
          </div>
          <DialogFooter>
            <button onClick={() => setShowCreate(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={submitCreate} disabled={saving} data-testid="create-submit"
              className="rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-bold text-white hover:bg-[#163a5f] disabled:opacity-60">{saving ? 'Saving…' : 'Add event'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>
);

const Panel = ({ title, icon, testid, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm" data-testid={testid}>
    <div className="mb-2 flex items-center gap-2 border-b border-white/10 pb-2 text-sm font-bold text-slate-200">{icon}{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const AttnRow = ({ e, overdue, onClick }) => {
  const c = catOf(e.source);
  return (
    <button onClick={onClick} className="block w-full rounded-lg border border-white/10 bg-white/[0.03] p-2.5 text-left hover:bg-white/[0.07]">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: c.color }} />
        <span className="text-xs font-bold text-white">{e.title}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${overdue ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-slate-300'}`}>{overdue ? 'Overdue' : mmddyy(e.date)}</span>
      </div>
      {e.detail && <div className="mt-0.5 pl-4 text-[11px] text-slate-400">{e.detail}</div>}
    </button>
  );
};

const MonthGrid = ({ sel, today, byDate, onDay, onEvent }) => {
  const grid = monthCells(sel.year, sel.month);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 sm:p-4">
      <div className="grid grid-cols-7 gap-[3px]">
        {DOW.map((d) => <div key={d} className="pb-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-[3px]" data-testid="calendar-grid">
        {grid.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="h-[92px] rounded-md bg-white/[0.02] sm:h-[110px]" />;
          const ds = iso(sel.year, sel.month, d);
          const items = byDate[ds] || [];
          const isToday = ds === today;
          const hasPay = items.some((e) => e.source === 'payroll');
          const show = items.length <= CAP ? items : items.slice(0, CAP - 1);
          const more = items.length <= CAP ? 0 : items.length - (CAP - 1);
          return (
            <div key={ds} data-testid={`calendar-day-${ds}`}
              className="relative flex h-[92px] min-w-0 flex-col overflow-hidden rounded-md bg-[#E4EBF1] p-1 sm:h-[110px]"
              style={isToday ? { boxShadow: 'inset 0 0 0 2px #6FA22B' } : undefined}>
              <span className="flex items-center justify-between">
                <button onClick={() => onDay(ds)} className={`flex h-[18px] min-w-[18px] items-center justify-center px-1 text-[11px] font-bold ${isToday ? 'rounded-full bg-[#6FA22B] text-white' : 'text-slate-700 hover:underline'}`}>{d}</button>
                {hasPay && <span title="Scheduled payroll date" className="flex h-[15px] w-[15px] items-center justify-center rounded-full bg-[#1E3A8A] text-white"><DollarSign size={9} strokeWidth={3} /></span>}
              </span>
              <span className="mt-0.5 flex min-h-0 flex-1 flex-col gap-[2px] overflow-hidden">
                {show.map((e, k) => <EventPillInline key={k} e={e} onClick={() => onEvent(e)} />)}
                {more > 0 && <button onClick={() => onDay(ds)} className="px-1 text-left text-[9px] font-bold text-slate-500" data-testid={`calendar-more-${ds}`}>+{more} more</button>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EventPillInline = ({ e, onClick }) => {
  const c = catOf(e.source);
  return (
    <button onClick={onClick} data-testid={`calendar-event-${e.record_id || e.title}`}
      className="flex w-full items-center rounded-[3px] px-1 py-[1px] text-left text-[9px] font-semibold"
      style={{ backgroundColor: c.color, color: c.text }}>
      <span className="min-w-0 truncate">{e.title}</span>
    </button>
  );
};

const WeekView = ({ days, today, byDate, onEvent }) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-7" data-testid="calendar-week">
    {days.map((ds) => {
      const [y, m, d] = ds.split('-').map(Number);
      const items = byDate[ds] || [];
      const isToday = ds === today;
      return (
        <div key={ds} data-testid={`calendar-week-day-${ds}`}
          className="rounded-xl border border-white/10 bg-white/[0.04] p-2" style={isToday ? { boxShadow: 'inset 0 0 0 2px #6FA22B' } : undefined}>
          <div className="mb-1.5 flex items-center justify-between border-b border-white/10 pb-1">
            <span className="text-[11px] font-bold uppercase text-slate-400">{DOW[new Date(y, m - 1, d).getDay()]}</span>
            <span className={`text-sm font-extrabold ${isToday ? 'text-[#8FC93A]' : 'text-white'}`}>{d}</span>
          </div>
          <div className="space-y-1">
            {items.length === 0 && <p className="py-2 text-center text-[10px] text-slate-500">—</p>}
            {items.map((e, k) => <EventPillInline key={k} e={e} onClick={() => onEvent(e)} />)}
          </div>
        </div>
      );
    })}
  </div>
);

const ListView = ({ events, today, onEvent }) => {
  const groups = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    return Object.keys(m).sort().map((d) => ({ date: d, items: m[d] }));
  }, [events]);
  if (groups.length === 0) return <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-10 text-center text-sm text-slate-400" data-testid="calendar-list-empty">No events in the next 90 days.</div>;
  return (
    <div className="space-y-3" data-testid="calendar-list">
      {groups.map(({ date, items }) => (
        <div key={date} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
          <div className={`mb-2 text-sm font-extrabold ${date === today ? 'text-[#8FC93A]' : 'text-white'}`}>{prettyDate(date)} {date === today && <span className="text-xs font-bold">· Today</span>}</div>
          <div className="space-y-1.5">
            {items.map((e, k) => {
              const c = catOf(e.source);
              return (
                <button key={k} onClick={() => onEvent(e)} data-testid={`calendar-list-item-${e.record_id || k}`}
                  className="flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5 text-left hover:bg-white/[0.07]">
                  <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: c.color }} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-white">{e.title}</span>
                    {e.detail && <span className="block truncate text-xs text-slate-400">{e.detail}</span>}
                  </span>
                  <span className="shrink-0 text-[10px] font-bold uppercase text-slate-500">{e.source_label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const DetailCard = ({ e, onOpen }) => {
  const c = catOf(e.source);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2">
        <span className="h-3.5 w-3.5 shrink-0 rounded-[3px] ring-1 ring-black/10" style={{ backgroundColor: c.color }} />
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{e.source_label}</span>
        {e.status && <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">{e.status}</span>}
      </div>
      <div className="mt-1 text-sm font-bold text-[#0E2A47]">{e.title}</div>
      {e.detail && <div className="text-xs text-slate-600">{e.detail}</div>}
      <button onClick={onOpen} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a5f]">Details</button>
    </div>
  );
};

export default CalendarModule;
